import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Form,
  Button,
  Alert,
  Spinner,
  Row,
  Col
} from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import { groupService, courseService } from '../../services/api';

const CreateGroupModal = ({ 
  show, 
  onHide, 
  editingGroup = null, 
  onGroupCreated, 
  onGroupUpdated 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    courseId: '',
    description: '',
    maxParticipants: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Populate form when editing
  useEffect(() => {
    if (editingGroup) {
      setFormData({
        name: editingGroup.name || '',
        courseId: editingGroup.course?.id || '',
        description: editingGroup.description || '',
        maxParticipants: editingGroup.maxParticipants?.toString() || ''
      });
      if (editingGroup.course) {
        setSelectedCourse({
          value: editingGroup.course.id,
          label: `${editingGroup.course.name} (${editingGroup.course.academicYear}/${editingGroup.course.academicYear + 1})`
        });
      }
    } else {
      // Reset form for creation
      setFormData({
        name: '',
        courseId: '',
        description: '',
        maxParticipants: ''
      });
      setSelectedCourse(null);
    }
  }, [editingGroup]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCourseChange = (selectedOption) => {
    setSelectedCourse(selectedOption);
    setFormData(prev => ({
      ...prev,
      courseId: selectedOption ? selectedOption.value : ''
    }));
  };

  const loadCoursesAsync = useCallback((inputValue) => {
    return new Promise((resolve) => {
      courseService.getCoursesSummary(inputValue).then(response => {
        if (response.success) {
          const formattedCourses = (response.data || []).map(course => ({
            value: course.id,
            label: `${course.name} (${course.academicYear}/${course.academicYear + 1})`
          }));
          resolve(formattedCourses);
        } else {
          resolve([]);
        }
      }).catch(() => resolve([]));
    });
  }, []);

  // Funzione debounced per react-select
  const debouncedLoadOptions = useCallback((inputValue, callback) => {
    // Cancella il timeout precedente se esiste
    if (debouncedLoadOptions.timeoutId) {
      clearTimeout(debouncedLoadOptions.timeoutId);
    }
    
    const timeoutId = setTimeout(() => {
      loadCoursesAsync(inputValue).then(callback);
    }, 300);
    
    // Memorizza l'ID del timeout corrente
    debouncedLoadOptions.timeoutId = timeoutId;
  }, [loadCoursesAsync]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.courseId) {
      setError('Compilare tutti i campi obbligatori');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const groupData = {
        name: formData.name,
        courseId: formData.courseId,
        description: formData.description || null,
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null
      };

      let response;
      
      if (editingGroup) {
        // Update existing group
        response = await groupService.updateGroup({
          id: editingGroup.id,
          ...groupData
        });
      } else {
        // Create new group
        response = await groupService.createGroup(groupData);
      }

      if (response.success) {
        // Reset form
        setFormData({
          name: '',
          courseId: '',
          description: '',
          maxParticipants: ''
        });
        setSelectedCourse(null);
        
        // Notify parent component
        if (editingGroup && onGroupUpdated) {
          onGroupUpdated(response.data);
        } else if (!editingGroup && onGroupCreated) {
          onGroupCreated(response.data);
        }
        
        // Close modal
        onHide();
      } else {
        setError(response.message || `Errore nella ${editingGroup ? 'modifica' : 'creazione'} del gruppo`);
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error saving group:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setFormData({
        name: '',
        courseId: '',
        description: '',
        maxParticipants: ''
      });
      setSelectedCourse(null);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="md" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editingGroup ? 'Modifica Gruppo' : 'Nuovo Gruppo'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nome Gruppo *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="es. Gruppo A"
                  required
                  disabled={loading}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Corso *</Form.Label>
                <AsyncSelect
                  value={selectedCourse}
                  onChange={handleCourseChange}
                  loadOptions={debouncedLoadOptions}
                  defaultOptions={false}
                  cacheOptions
                  isSearchable
                  isClearable
                  isDisabled={loading || editingGroup}
                  placeholder="Cerca un corso..."
                  noOptionsMessage={({ inputValue }) => 
                    inputValue ? "Nessun corso trovato" : "Digita per cercare"
                  }
                  loadingMessage={() => "Caricamento..."}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
                      '&:hover': {
                        borderColor: '#adb5bd'
                      }
                    })
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Descrizione</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Descrizione del gruppo..."
              disabled={loading}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Numero Massimo Partecipanti</Form.Label>
            <Form.Control
              type="number"
              min="1"
              name="maxParticipants"
              value={formData.maxParticipants}
              onChange={handleChange}
              placeholder="Lascia vuoto per nessun limite"
              disabled={loading}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={handleClose}
            disabled={loading}
          >
            Annulla
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading}
          >
            {loading && <Spinner animation="border" size="sm" className="me-2" />}
            {editingGroup ? 'Aggiorna Gruppo' : 'Crea Gruppo'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateGroupModal;